import { dataService } from '@app/modules/data';
import { DataObject, ObjectOptions } from './base';

import { PlaceService } from './svcplace';

export interface _SmartDrawerDevice {
    rasppi: number;
    status: string;
    name: string;
    model: string;
};

interface _SmartDrawerDeviceData extends _SmartDrawerDevice {
    objid?: number;
    _uuid?: string;
    created?: Date;
    updated?: Date;
};

abstract class SmartDrawerDeviceData extends DataObject {
    protected _smartdrawer: _SmartDrawerDeviceData = {
        rasppi: null,
        status: null,
        name: null,
        model: null
    };

    constructor(table: string, objid: string, data: dataService, objoptions: ObjectOptions){
        super(table, objid, data, objoptions);
        this._smartdrawer.created = new Date();
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get created(){
        return this._smartdrawer.created;
    }

    get updated(): Date {
        return this._smartdrawer.updated || this._smartdrawer.created;
    }

    get rasppi() : PlaceService {
        return this._children['rasppi'] || null;
    }

    set rasppi(value: PlaceService){
        if (this.SetChild('rasppi', value, 'rasppi')){
            // Only mark as ToUpdate if it's not a new device (ToInsert)
            if (!this.ToInsert){
                this.ToUpdate = true;
            }
        }
    }

    get status(): string{
        return this._smartdrawer.status;
    }

    set status(value: string){
        if (this.patchValue(this._smartdrawer, 'status', value)){
            // Only mark as ToUpdate if it's not a new device (ToInsert)
            if (!this.ToInsert){
                this.ToUpdate = true;
            }
        }
    }

    get name(): string{
        return this._smartdrawer.name;
    }

    set name(value: string){
        if (this.patchValue(this._smartdrawer, 'name', value)){
            // Only mark as ToUpdate if it's not a new device (ToInsert)
            if (!this.ToInsert){
                this.ToUpdate = true;
            }
        }
    }

    get model(): string{
        return this._smartdrawer.model;
    }

    set model(value: string){
        if (this.patchValue(this._smartdrawer, 'model', value)){
            // Only mark as ToUpdate if it's not a new device (ToInsert)
            if (!this.ToInsert){
                this.ToUpdate = true;
            }
        }
    }

    /****************************/
    /* COMMIT OPERATION         */
    /****************************/

    protected get Change() {
        return {
            rasppi: this._smartdrawer.rasppi,
            status: this._smartdrawer.status,
            name: this._smartdrawer.name,
            model: this._smartdrawer.model
        };
    }

    protected get Depend() {
        return {
            rasppi: { item: this.rasppi, relation_info: { to: 'smartdrawers', by: 'rasppi' } }
        };
    }

    protected get Children(){
        return [ /* empty */];
    }

    /****************************/
    /* DATA OBJECT              */
    /****************************/
    
    private _patchData(_smartdrawer: _SmartDrawerDevice){
        let _toUpdate = false;

        _toUpdate = this.patchValue(this._smartdrawer, 'rasppi', _smartdrawer['rasppi']) || _toUpdate;
        _toUpdate = this.patchValue(this._smartdrawer, 'status', _smartdrawer['status']) || _toUpdate;
        _toUpdate = this.patchValue(this._smartdrawer, 'name', _smartdrawer['name']) || _toUpdate;
        _toUpdate = this.patchValue(this._smartdrawer, 'model', _smartdrawer['model']) || _toUpdate;

        return _toUpdate;
    }    

    set Data(_smartdrawer: _SmartDrawerDevice){
        this.patchValue(this._smartdrawer, 'created', _smartdrawer['created']);
        this.patchValue(this._smartdrawer, 'updated', _smartdrawer['updated']);
        
        if (this._patchData(_smartdrawer)){
            this.ToUpdate = true;
        }
    }

    get Info(){
        return this._smartdrawer;
    }

    set Info(value){
        this.DoPatchValues(value);
    }

    private DoPatchValues(_smartdrawer: _SmartDrawerDevice){
        this._patchData(_smartdrawer);

        if (_smartdrawer['rasppi']){
            let _objid = _smartdrawer['rasppi'].toString();
            this.SetChild('rasppi', new PlaceService(_objid, this.data, this._objoptions), 'rasppi')
        }
        else {
            this.SetChild('rasppi', null, 'rasppi');
        }
    }

    private _ddbb(info): _SmartDrawerDeviceData {
        let _smartdrawer: _SmartDrawerDeviceData = {
            objid: info['objid'] ? parseInt(info['objid']) : null,
            created: new Date(Date.parse(this.mysqlToDateStr(info['created']))),
            updated: new Date(Date.parse(this.mysqlToDateStr(info['updated']))),
            rasppi: info['rasppi'] ? parseInt(info['rasppi']) : null,
            status: info['status'],
            name: info['name'],
            model: info['model']
        };
        return _smartdrawer;
    }

    protected _OnUpdate(info){
        let _smartdrawer = this._ddbb(info);
        this.patchValue(this._smartdrawer, 'objid', _smartdrawer['objid']);
        this.patchValue(this._smartdrawer, 'created', _smartdrawer['created']);
        this.patchValue(this._smartdrawer, 'updated', _smartdrawer['updated']);
        this.DoPatchValues(_smartdrawer);
    }
}

export class SmartDrawerDevice extends SmartDrawerDeviceData {
    constructor(objid: string, data: dataService, objoptions: ObjectOptions = null){
        super('SMARTDRAWER', objid, data, objoptions);
    }

    Copy(store: Array<DataObject> = []): SmartDrawerDevice {
        return this._Copy(store) as SmartDrawerDevice;
    }

    /****************************/
    /* CLASS MEMBERS            */
    /****************************/

    get status(): string {
        return super.status;
    }

    set status(value: string){
        if (this.status == 'DE'){
            return;     // cannot modify deleted items
        }

        super.status = value;

        if (this.rasppi){
            if ((this.status == 'DE') && (this.ToInsert) && (!this.CopyOf || this.CopyOf.ToInsert)){
                this.rasppi.DelSmartDrawer(this);
            }
            else {
                this.rasppi.DoRefresh('SMARTDRAWER');
            }    
        }
    }

    /****************************/
    /* CUSTOM METHODS           */
    /****************************/

    get IsValid(){
        return (this.status && this.status != 'DE');
    }

    get IsDeleted(){
        return (this.status == 'DE');
    }
}
